/**
 * GitHub account connection settings.
 * Connect/disconnect GitHub, shows account info, project board picker.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  isGitHubConnected,
  getCachedGitHubUser,
  getSelectedProject,
  startGitHubOAuth,
  clearGitHubToken,
  fetchGitHubUser,
  fetchGitHubProjects,
  setSelectedProject,
  clearSelectedProject,
  checkAppInstallation,
  getInstallationStatus,
  getInstallUrl,
  type GitHubUser,
  type GitHubProject,
} from '../utils/github';
import styles from './GitHubSettings.module.css';

export function GitHubSettings() {
  const [connected, setConnected] = useState(isGitHubConnected());
  const [user, setUser] = useState<GitHubUser | null>(getCachedGitHubUser());
  const [projects, setProjects] = useState<GitHubProject[]>([]);
  const [selected, setSelected] = useState<GitHubProject | null>(getSelectedProject());
  const [installStatus, setInstallStatus] = useState<'installed' | 'not-installed' | null>(getInstallationStatus());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user and projects on mount if connected
  useEffect(() => {
    if (!connected) return;

    setLoading(true);
    Promise.all([
      fetchGitHubUser().catch(() => null),
      fetchGitHubProjects().catch(() => []),
      checkAppInstallation().catch(() => false),
    ]).then(([fetchedUser, fetchedProjects, installed]) => {
      if (fetchedUser) setUser(fetchedUser);
      setProjects(fetchedProjects);
      setInstallStatus(installed ? 'installed' : 'not-installed');
      // Re-check connection (token might have been cleared if expired)
      setConnected(isGitHubConnected());
    }).finally(() => setLoading(false));
  }, [connected]);

  const handleConnect = useCallback(async () => {
    try {
      setError(null);
      await startGitHubOAuth();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start OAuth');
    }
  }, []);

  const handleDisconnect = useCallback(() => {
    clearGitHubToken();
    setConnected(false);
    setUser(null);
    setProjects([]);
    setSelected(null);
  }, []);

  const handleProjectSelect = useCallback((projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setSelectedProject(project);
      setSelected(project);
    } else {
      clearSelectedProject();
      setSelected(null);
    }
  }, [projects]);

  if (!connected) {
    return (
      <div className={styles.container}>
        <div className={styles.sectionTitle}>GitHub</div>
        <button className={styles.connectBtn} onClick={() => void handleConnect()}>
          Connect GitHub Account
        </button>
        {error && <div className={styles.error}>{error}</div>}
        <div className={styles.hint}>
          Connect your GitHub account to view project boards
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.sectionTitle}>GitHub</div>

      {/* Connected account info */}
      <div className={styles.accountRow}>
        {user && (
          <>
            <img src={user.avatar_url} alt="" className={styles.avatar} />
            <div className={styles.accountInfo}>
              <span className={styles.accountName}>{user.name || user.login}</span>
              <span className={styles.accountLogin}>@{user.login}</span>
            </div>
          </>
        )}
        <button className={styles.disconnectBtn} onClick={handleDisconnect}>
          Disconnect
        </button>
      </div>

      {/* App installation prompt */}
      {installStatus === 'not-installed' && (
        <div className={styles.installBanner}>
          <div className={styles.installText}>
            App not installed — install it to access your project boards
          </div>
          <a
            href={getInstallUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.installBtn}
          >
            Install GitHub App
          </a>
        </div>
      )}

      {/* Project board picker */}
      {installStatus !== 'not-installed' && (
        <div className={styles.projectSection}>
          <label className={styles.label}>Project Board</label>
          {loading ? (
            <div className={styles.hint}>Loading projects…</div>
          ) : projects.length > 0 ? (
            <select
              className={styles.select}
              value={selected?.id || ''}
              onChange={(e) => handleProjectSelect(e.target.value)}
            >
              <option value="">Select a project…</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
          ) : (
            <div className={styles.hint}>No projects found</div>
          )}
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}
